// Frontend mirror of the AcroForm field map for backend/assets/calor/CGL104228_template.pdf.
// Kept in sync with backend/services/calorCde/fieldNames.js.  Not used at
// runtime -- the PDF is filled server-side -- but useful for documentation
// and for future client-side preview work.
//
// Pass 1 reconciliation: only the fields that actually exist in the prepared
// template.  Fields the React form sends but the PDF doesn't have are
// drawn as overlay text by the backend (see OVERLAYS in the server map).

export const HEADER_FIELDS = {
  customerName: { name: 'customer_name', type: 'text' },
  installationAddress: { name: 'depot_address', type: 'text' },
  installationCategory: { name: 'Installation_Cat', type: 'text' },
  installationUse: { name: 'Installation_Use', type: 'text' },
  deliveryPointCode: { name: 'delivery_point_code', type: 'text' },
  wseReference: { name: 'wse_reference', type: 'text' },
  certificateNumber: { name: 'certificate_number', type: 'text' },
  documentTypeInspection: { name: 'inspection_check', type: 'check' },
  documentTypeReport: { name: 'report_check', type: 'check' },
  documentTypeCertificate: { name: 'certificate_check', type: 'check' },
  examAnnual: { name: 'examination_type_annual', type: 'text' },
  examInService: { name: 'examination_type_inservice', type: 'text' },
  examThorough: { name: 'examination_type_thorough', type: 'text' },
};

export const TANK_FIELDS = {
  serialNumber: { name: 'tank_serial_number', type: 'text' },
  nameplateTestCode: { name: 'nameplate_test_code', type: 'text' },
  typeCode: { name: 'type_code', type: 'text' },
  manufacturersCode: { name: 'manufacturers_code', type: 'text' },
  manufacturersTestDate: { name: 'manufacturers_test_date', type: 'text' },
  previousInserviceDateCode: { name: 'previous_inservice_date_code', type: 'text' },
  previousThoroughDateCode: { name: 'previous_thorough_date_code', type: 'text' },
  tankSize: { name: 'tank_size', type: 'text' },
  designCode: { name: 'design_code', type: 'text' },
  designPressure: { name: 'design_pressure', type: 'text' },
  colourGreen: { name: 'colour_green', type: 'check' },
  colourWhite: { name: 'colour_white', type: 'check' },
  useVap: { name: 'use_vap', type: 'check' },
  useTopLiq: { name: 'use_top_liq', type: 'check' },
  useBottomLiq: { name: 'use_bottom_liq', type: 'check' },
};

export const CHECKLIST_FIELDS = {
  externalSurfacesExamined: { name: 'external_surfaces_check', type: 'text' },
  allValvesFittingsExamined: { name: 'valves_fittings_ancillary_check', type: 'text' },
  reliefValvesReplacedRainCaps: { name: 'reliefvalve_replaced_raincaps_check', type: 'text' },
  fixedLiquidLevelChecked: { name: 'fixedlevel_check', type: 'text' },
  floatGaugeScrewsChecked: { name: 'float_guage_check', type: 'text' },
  hoodServiceableLockable: { name: 'hoodserviceable_check', type: 'text' },
  // Item 15 is no longer offered on the form; the backend always marks it N/A.
  earthingPointsSatisfactory: { name: 'earthing_point_check', type: 'text' },
  allConnectionsCheckedLeaks: { name: 'allconnections_checkedforleaks', type: 'text' },
  vapourOffTakeValveOperational: { name: 'vapourvalve_operationalcheck', type: 'text' },
  acmeFillerValveVisuallyChecked: { name: 'fille_vave_check', type: 'text' },
  newFillerSealingWasherFitted: { name: 'washerfitted_capsatisfactory_check', type: 'text' },
  manualShutoffChecked: { name: 'manual_shutoff_check', type: 'text' },
  pressureGaugeChecked: { name: 'pressure_guage_check', type: 'text' },
};

// Paintwork sub-decision -- maps to the Repaint / Cleaning PDF checkboxes.
export const PAINTWORK_FIELDS = {
  repaint: { name: 'Repaint', type: 'check' },
  cleaning: { name: 'Cleaning', type: 'check' },
};

export const SHELL_COL_COUNT = 12;

const buildShellNames = (base, offset) =>
  Array.from({ length: SHELL_COL_COUNT }, (_, i) => {
    const n = i + 1 + offset;
    return n === 1 ? base : `${base}_${n}`;
  });

// Main shell only -- the ENDR / ENDL columns shown on the PDF are populated
// by panel1 / panel3 (the end-cap circular panels), not by panel2 / panel4.
export const ULTRASONIC_FIELDS = {
  panel2: {
    tdc30: { shell: buildShellNames('SHELL30 TDC', 0) },
    mid90: { shell: buildShellNames('SHELL90', 0) },
    bdc20: { shell: buildShellNames('SHELL20 BDC', 0) },
  },
  panel4: {
    tdc30: { shell: buildShellNames('SHELL30 TDC', SHELL_COL_COUNT) },
    mid90: { shell: buildShellNames('SHELL90', SHELL_COL_COUNT) },
    bdc20: { shell: buildShellNames('SHELL20 BDC', SHELL_COL_COUNT) },
  },
};

// Main-shell panels rendered as a linear grid.
export const ULTRASONIC_GRID_KEYS = Object.keys(ULTRASONIC_FIELDS);
export const ULTRASONIC_ROW_KEYS = ['tdc30', 'mid90', 'bdc20'];

// End-cap panels rendered as a circular layout.
export const ULTRASONIC_END_PANEL_KEYS = ['panel1', 'panel3'];

// All panels rendered in the React form (used by the validation pass).
export const ULTRASONIC_PANEL_KEYS = ['panel1', 'panel2', 'panel3', 'panel4'];

export const ULTRASONIC_GRID_LABELS = {
  panel1: 'End Shell Right Panel 1 (Data Plate End)',
  panel2: 'Main Shell Front Panel 2 (Relief Valve RHS)',
  panel3: 'End Shell Left Panel 3',
  panel4: 'Main Shell Back Panel 4 (Relief Valve LHS)',
};

export const ULTRASONIC_ROW_LABELS = {
  tdc30: 'TDC 30°',
  mid90: '90°',
  bdc20: 'BDC 20°',
};

// Shells = the physical sections of the vessel.  Each shell has ONE control
// reading shared by every panel attached to it.  Panels 2 and 4 are both
// the Main Shell (just opposite sides) so they share the same control.
export const ULTRASONIC_SHELL_KEYS = ['endShellRight', 'mainShell', 'endShellLeft'];

export const ULTRASONIC_SHELL_LABELS = {
  endShellRight: 'End Shell Right (Data Plate End)',
  mainShell: 'Main Shell',
  endShellLeft: 'End Shell Left',
};

// Which panels live on which shell, in display order within the group.
export const ULTRASONIC_SHELL_PANELS = {
  endShellRight: ['panel1'],
  mainShell: ['panel2', 'panel4'],
  endShellLeft: ['panel3'],
};

// Inverse: which shell's control feeds each panel's ±10% rule.
export const ULTRASONIC_PANEL_CONTROL_MAP = {
  panel1: 'endShellRight',
  panel2: 'mainShell',
  panel3: 'endShellLeft',
  panel4: 'mainShell',
};

// Optional SECOND main shell.  On the PDF it reuses the same 12 physical
// columns per main-shell row: the primary panel's readings, then ONE separator
// column (rendered with the N/A diagonal), then the additional panel's
// readings.  Panel 5 = front (continues Panel 2); Panel 6 = back (continues
// Panel 4).  Both are validated against the second main-shell control.
export const ULTRASONIC_ADDITIONAL_PANEL_KEYS = ['panel5', 'panel6'];
export const ULTRASONIC_ADDITIONAL_CONTROL_KEY = 'mainShell2';
export const ULTRASONIC_ADDITIONAL_PANEL_CONTROL_MAP = {
  panel5: 'mainShell2',
  panel6: 'mainShell2',
};
export const ULTRASONIC_ADDITIONAL_GRID_LABELS = {
  panel5: 'Main Shell 2 — Front (Panel 5, continues Panel 2)',
  panel6: 'Main Shell 2 — Back (Panel 6, continues Panel 4)',
};
// Which primary panel each additional panel shares its PDF row with.
export const ULTRASONIC_ADDITIONAL_PRIMARY_MAP = { panel5: 'panel2', panel6: 'panel4' };
// One column is consumed by the dashed separator between the two shells, so the
// combined readings on a shared row must not exceed this many cells.
export const ULTRASONIC_SHARED_ROW_CAPACITY = SHELL_COL_COUNT - 1;

// End-cap reading layout.  Six readings per end shell, taken in a circle
// around the dished end.  Angles are measured CLOCKWISE from TDC (12 o'clock):
//   +30  / +90  / +160  -> right-half of the end cap (R1, R2, R3)
//   -30  / -90  / -160  -> left-half of the end cap  (R4, R5, R6)
// Reading order matches the inspector's on-tank sweep and the PDF's row
// layout (TDC30 / 90 / BDC20 in two stacked grids -- see backend
// END_READING_FIELDS).
export const END_READING_COUNT = 6;

export const END_READING_POSITIONS = [
  { idx: 0, angle: 30,   label: '30° past TDC',  side: 'R' },
  { idx: 1, angle: 90,   label: '90° past TDC',  side: 'R' },
  { idx: 2, angle: 160,  label: '20° from BDC',  side: 'R' },
  { idx: 3, angle: -30,  label: '30° past TDC',  side: 'L' },
  { idx: 4, angle: -90,  label: '90° past TDC',  side: 'L' },
  { idx: 5, angle: -160, label: '20° from BDC',  side: 'L' },
];

// Human-readable label for the violation modal.
export const END_READING_LABEL = (idx) => {
  const pos = END_READING_POSITIONS[idx];
  if (!pos) return `Reading ${idx + 1}`;
  return `Reading ${idx + 1}: ${pos.label} (${pos.side})`;
};

// Empty main-shell grid (3 rows of shell[12]).
const emptyMainShellPanel = () => ({
  tdc30: { shell: Array(SHELL_COL_COUNT).fill('') },
  mid90: { shell: Array(SHELL_COL_COUNT).fill('') },
  bdc20: { shell: Array(SHELL_COL_COUNT).fill('') },
});

// Empty end-cap panel (6 circular readings).
const emptyEndPanel = () => ({
  readings: Array(END_READING_COUNT).fill(''),
});

export const buildEmptyUltrasonic = () => ({
  // Off by default; the inspector opts in via "Add another main shell".
  additionalShell: false,
  controls: {
    mainShell: '',
    endShellRight: '',
    endShellLeft: '',
    mainShell2: '',
  },
  panel1: emptyEndPanel(),
  panel2: emptyMainShellPanel(),
  panel3: emptyEndPanel(),
  panel4: emptyMainShellPanel(),
  // Optional second main shell (front / back). Empty until enabled.
  panel5: emptyMainShellPanel(),
  panel6: emptyMainShellPanel(),
});

// Tolerance for the per-shell control: every reading on every panel
// attached to a shell must sit within this fraction either side of that
// shell's control reading.  ±10% by default.
export const ULTRASONIC_CONTROL_TOLERANCE = 0.10;

// WSE Failure guidance text shown in the warning modal verbatim.
export const ULTRASONIC_FAILURE_GUIDANCE = (
  'Measure surrounding areas: they shall be measured at 25mm intervals and the '
  + 'thinning area marked on the vessel surface. '
  + 'The lamination defects are normally indicated by an abrupt change in thickness at the thinning edges. '
  + 'Corrosion defects are normally indicated by a gradual return to full thickness at the edges. '
  + 'Please see WSE for Failure guidance.'
);

export const RELIEF_VALVE_FIELDS = {
  manufacturer: { name: 'manufacturer_type', type: 'text' },
  type: { name: 'reliefvalve_type', type: 'text' },
  setPressure: { name: 'relief_valve_pressure', type: 'text' },
  testDates: { name: 'PRV_date', type: 'text' },
};

export const SIGN_OFF_FIELDS = {
  employerName: { name: 'Employer_Name', type: 'text' },
  inspectorName: { name: 'inspector_name', type: 'text' },
  dateExamined: { name: 'date_examination', type: 'text' },
  inspectorCertificateNumber: { name: 'inspector_number', type: 'text' },
  vesselSatisfactoryYes: { name: 'vess_satisfactory_yes', type: 'radio', option: 'Yes_3' },
  vesselSatisfactoryNo: { name: 'vess_satisfactory_no', type: 'radio', option: 'No_3' },
  changesSafeLimitsYes: { name: 'safeoperating_limits_yes', type: 'radio', option: 'Yes_5' },
  changesSafeLimitsNo: { name: 'safeoperating_limits_no', type: 'radio', option: 'No_5' },
  repairsRequiredYes: { name: 'mods_required_yes', type: 'check' },
  repairsRequiredNo: { name: 'mods_required_no', type: 'check' },
  wseSuitableYes: { name: 'WSE_suitable_yes', type: 'check' },
  wseSuitableNo: { name: 'WSE_suitable_no', type: 'check' },
  upliftRequiredGroup: {
    name: 'UPLIFT REQUIRED  EXCHANGE FORM COMPLETED',
    type: 'radio',
    optionYes: 'YES',
    optionNo: 'NO',
  },
  additionalExamination: {
    name: 'additional_examination',
    type: 'radio',
    optionYes: 'Yes_2',
    optionNo: 'No_2',
  },
  inspectorSignature: { name: 'inspector_signature_es_:signer:signature', type: 'image' },
};

export const MATERIAL_ROW_COUNT = 3;
